import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {CdkDragDrop, DragDropModule} from '@angular/cdk/drag-drop';
import {BuilderStateService, isUnaddressableFieldKey} from '../../services/builder-state.service';
import {FormBlock, FormDefinition, ReviewType} from '../../../shared/models/form-schema.model';
import {RuleEditorComponent} from '../rule-editor/rule-editor.component';
import {MatDialog} from '@angular/material/dialog';
import {
  RequiredReviewsPopupComponent
} from '../required-reviews-popup/required-reviews-popup.component';
import {
  ValidityPeriodPopupComponent
} from '../validity-period-popup/validity-period-popup.component';
import {GlobalAlertService} from "../../../../../_services/global-alert.service";
import {AlertLevel} from "../../../../../enums/alert-level";

/** Palette entry shown in the "add element" controls. */
export interface PaletteItem {
  type: string;
  label: string;
}

export const FIELD_PALETTE: PaletteItem[] = [
  {type: 'text', label: 'Text'},
  {type: 'textarea', label: 'Textbereich'},
  {type: 'email', label: 'E-Mail'},
  {type: 'phone', label: 'Telefon'},
  {type: 'number', label: 'Zahl'},
  {type: 'date', label: 'Datum'},
  {type: 'time', label: 'Uhrzeit'},
  {type: 'boolean', label: 'Ja/Nein'},
  {type: 'select', label: 'Auswahl'},
  {type: 'multiselect', label: 'Mehrfachauswahl'},
  {type: 'radio', label: 'Optionsfeld'},
  {type: 'checkbox-group', label: 'Kontrollkästchen'},
  {type: 'file', label: 'Datei'},
  {type: 'signature', label: 'Unterschrift'},
  // Inherently-sensitive types — always encrypted + masked (see SENSITIVE_FIELD_TYPES).
  {type: 'iban', label: 'IBAN'},
  {type: 'bank', label: 'Bankverbindung'},
  {type: 'idnumber', label: 'Ausweisnummer'},
  {type: 'taxid', label: 'Steuer-ID'},
  {type: 'socialsecurity', label: 'SV-Nummer'}
];

export const STRUCTURE_PALETTE: PaletteItem[] = [
  {type: 'group', label: 'Gruppe'},
  {type: 'list', label: 'Wiederholbare Liste'},
  {type: 'static-text', label: 'Statischer Text'}
];

@Component({
  selector: 'forms-builder-block-list',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule],
  template: `
    <div cdkDropList
         [cdkDropListData]="blocks"
         (cdkDropListDropped)="drop($event)"
         class="min-h-[64px] bg-gray-50/50 border-2 border-dashed border-gray-200 rounded-2xl p-2 sm:p-2.5 transition-all duration-200 hover:bg-gray-100/50 hover:border-gray-300">

      @if (blocks.length === 0) {
        <div class="h-full flex flex-col items-center justify-center text-gray-400 text-sm font-medium py-6 space-y-2 opacity-60">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <span class="text-center">Elemente hierher ziehen oder die Schaltflächen verwenden.</span>
        </div>
      }

      @for (block of blocks; track block.id; let i = $index) {
        <div cdkDrag
             class="bg-white border border-gray-100 rounded-xl p-2.5 mb-2 shadow-sm hover:shadow-lg transition-all duration-200 group relative">
          <div class="flex justify-between items-center gap-2">
            <div class="flex items-center gap-2 sm:gap-4 min-w-0">
              <div cdkDragHandle
                   class="p-2 bg-gray-50 rounded-xl text-gray-300 group-hover:bg-theme-primary-50 group-hover:text-theme-primary-500 transition-all duration-200 cursor-move flex-shrink-0 touch-none">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd"
                        d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                        clip-rule="evenodd"/>
                </svg>
              </div>
              <div class="min-w-0">
                <div class="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <span class="text-[10px] font-bold tracking-widest text-theme-primary-500 uppercase bg-theme-primary-50/50 px-2 py-0.5 rounded-lg border border-theme-primary-100/50">{{ block.type }}</span>
                  <span class="font-bold text-gray-900 tracking-tight truncate">{{ block.label || 'Unbenannter Block' }}</span>

                  @if (block.type !== 'static-text' && getKey(block)) {
                    <div class="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-lg group/key cursor-pointer hover:bg-theme-primary-50 transition-colors"
                         (click)="copyToClipboard(getKey(block)); $event.stopPropagation()">
                      <span class="text-[10px] font-mono font-medium text-gray-400 group-hover/key:text-theme-primary-600 transition-colors">{{ getKey(block) }}</span>
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-gray-300 group-hover/key:text-theme-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"/>
                      </svg>
                    </div>
                  }
                  @if (hasConditions(block)) {
                    <span title="Bedingte Logik aktiv" class="text-[10px] font-bold text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded-md border border-amber-100">⚡ Logik</span>
                  }
                  <!--
                    #470: a key the runtime cannot address breaks the whole form silently, so a definition that
                    already carries one (imported, hand-edited, built before the guard) must be visible here —
                    not only once the author happens to open that block's editor.
                    #481: it fires ONLY for a genuinely unresolvable key (a dot). A legacy key that merely
                    misses the house charset (fullName, a hyphen) works perfectly and must never be painted
                    as broken.
                  -->
                  @if (keyUnaddressable(block)) {
                    <span title="Dieser Schlüssel enthält einen Punkt und kann deshalb nicht aufgelöst werden: das Feld wird nicht angezeigt, Regeln darauf greifen nicht und es blockiert das Formular."
                          class="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-md border border-red-100">⚠ Ungültiger Schlüssel</span>
                  }
                </div>
              </div>
            </div>

            <div class="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-200">
              <button (click)="state.startEditingBlock(pageId, block)"
                      class="px-3 py-1.5 bg-theme-primary-50 text-theme-primary-600 hover:bg-theme-primary-100 rounded-xl transition-all duration-200 text-xs font-bold shadow-sm">
                Bearbeiten
              </button>
              <button (click)="state.removeBlock(pageId, block.id)" aria-label="Entfernen"
                      class="px-3 py-1.5 bg-red-50 text-red-500 hover:bg-red-100 rounded-xl transition-all duration-200 text-xs font-bold shadow-sm">
                Entfernen
              </button>
            </div>
          </div>

          <!-- Nested Blocks for Group/List -->
          <div *ngIf="block.type === 'group' || block.type === 'list'"
               class="mt-3 pl-2 sm:pl-3 border-l-2 border-theme-primary-50">
            <div class="mb-2 flex items-center space-x-2">
              <span class="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Inhalt</span>
              <div class="h-px bg-gray-100 flex-grow"></div>
            </div>

            <forms-builder-block-list [blocks]="asContainer(block).blocks" [pageId]="pageId"
                                      [parentBlockId]="block.id"></forms-builder-block-list>

            <div class="mt-4 flex flex-wrap gap-2">
              @for (item of fieldPalette; track item.type) {
                <button (click)="state.addBlock(pageId, item.type, block.id)"
                        class="px-2.5 py-1.5 bg-white border border-gray-100 rounded-xl text-[10px] font-bold text-gray-500 hover:border-theme-primary-200 hover:text-theme-primary-600 transition-all duration-200 shadow-sm active:scale-95">
                  + {{ item.label }}
                </button>
              }
            </div>
          </div>
        </div>
      }
    </div>
  `
})
export class BuilderBlockListComponent {
  @Input() blocks: FormBlock[] = [];
  @Input() pageId!: string;
  @Input() parentBlockId?: string;

  readonly fieldPalette = FIELD_PALETTE;

  constructor(public state: BuilderStateService, private alertService: GlobalAlertService) {
  }

  drop(event: CdkDragDrop<FormBlock[]>) {
    this.state.reorderBlocks(this.pageId, event.previousIndex, event.currentIndex, this.parentBlockId);
  }

  asContainer(block: FormBlock): any {
    return block;
  }

  hasConditions(block: FormBlock): boolean {
    return !!(block.visibleWhen || block.enabledWhen || block.requiredWhen);
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.alertService.createAndShow("Information", "Schlüssel in die Zwischenablage kopiert.", AlertLevel.info);
    });
  }

  getKey(block: any): string {
    return block.key || '';
  }

  /**
   * True when the runtime genuinely cannot address a block's data key (#470/#481) — see
   * {@link isUnaddressableFieldKey}. Narrow on purpose: this paints a red "broken" badge on somebody's stored
   * form, so it may only fire on a key that really does break. `static-text` is exempt — its key never becomes
   * a form control.
   */
  keyUnaddressable(block: FormBlock): boolean {
    return block.type !== 'static-text' && 'key' in block
      && isUnaddressableFieldKey((block as { key: string }).key);
  }
}

@Component({
  selector: 'forms-builder-canvas',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule, BuilderBlockListComponent, RuleEditorComponent],
  styles: `
    textarea, input {
      @apply rounded-md -mx-1.5 px-1.5;
    }
  `,
  template: `
    <div class="builder-canvas pb-20">
      <div *ngIf="state.definition$ | async as def" class="max-w-3xl mx-auto">
        <!-- Form Header Edit -->
        <div class="mb-5 flex flex-col space-y-1 px-2">
          <input [ngModel]="def.title" (ngModelChange)="updateTitle($event)"
                 class="text-2xl sm:text-3xl font-bold bg-transparent w-full placeholder-gray-200 tracking-tight text-gray-900"
                 placeholder="Formular-Titel"/>
          <textarea [ngModel]="def.description" (ngModelChange)="updateDescription($event)"
                    class="text-sm sm:text-base text-gray-400 bg-transparent w-full resize-none placeholder-gray-200 leading-relaxed rounded-md"
                    placeholder="Beschreibung hinzufügen..."></textarea>
          <!-- Form-level settings: which human checks a submission needs, and how long it stays valid. Both open a
               popup that applies its changes to the definition immediately. -->
          <div class="mt-2 flex flex-wrap gap-2">
            <button type="button" (click)="openReviewsDialog(def)"
                    class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50">
              <svg class="size-4 text-theme-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="m9 12.75 2.25 2.25L15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
              </svg>
              Erforderliche Prüfungen
              @if (requiredReviewCount(def) > 0) {
                <span class="rounded-full bg-theme-primary-50 px-2 py-0.5 text-xs font-semibold text-theme-primary-700">{{ requiredReviewCount(def) }}</span>
              }
            </button>
            <button type="button" (click)="openValidityDialog(def)"
                    class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50">
              <svg class="size-4 text-theme-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6l4 2m5-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
              </svg>
              Gültigkeitsdauer
              <span class="rounded-full bg-theme-primary-50 px-2 py-0.5 text-xs font-semibold text-theme-primary-700">{{ validityLabel(def) }}</span>
            </button>
          </div>
        </div>

        @for (page of def.pages; track page.id; let pageIndex = $index) {
          <div class="bg-white rounded-2xl shadow-lg shadow-gray-200/50 p-3 sm:p-4 mb-5 relative border border-gray-100 animate-in fade-in slide-in-from-bottom-4 duration-500"
               [style.animation-delay.ms]="pageIndex * 100">
            <div class="inline-flex mb-3 w-full">
              <div class="flex w-full items-center gap-2">
                <span class="flex items-center justify-center w-7 h-7 aspect-square rounded-full bg-theme-primary-500 text-white text-xs font-bold flex-shrink-0">{{ pageIndex + 1 }}</span>
                <input [(ngModel)]="page.title" (ngModelChange)="state.touch()"
                       class="text-lg sm:text-xl font-bold bg-transparent border-none focus:ring-0 text-gray-800 tracking-tight w-full min-w-0"
                       placeholder="Seiten-Titel"/>
                <button (click)="state.removePage(page.id)" aria-label="Seite löschen"
                        class="px-2 sm:px-3 py-1.5 text-xs font-bold text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 flex-shrink-0 whitespace-nowrap">
                  Löschen
                </button>
              </div>
            </div>

            <forms-builder-block-list [blocks]="page.blocks" [pageId]="page.id"></forms-builder-block-list>

            <!-- Add Block Controls -->
            <div class="mt-3 pt-2.5 border-t border-gray-50 space-y-2.5">
              <div>
                <p class="text-[10px] font-black text-gray-300 uppercase tracking-[0.2em] mb-1.5">Eingabefelder</p>
                <div class="flex flex-wrap gap-1.5">
                  @for (item of fieldPalette; track item.type) {
                    <button (click)="state.addBlock(page.id, item.type)"
                            class="px-2.5 py-1.5 bg-white border border-gray-100 rounded-lg text-xs font-bold text-gray-600 hover:border-theme-primary-200 hover:text-theme-primary-600 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95 flex items-center">
                      <span class="mr-1 text-theme-primary-400">+</span>{{ item.label }}
                    </button>
                  }
                </div>
              </div>
              <div>
                <p class="text-[10px] font-black text-gray-300 uppercase tracking-[0.2em] mb-1.5">Layout & Struktur</p>
                <div class="flex flex-wrap gap-1.5">
                  @for (item of structurePalette; track item.type) {
                    <button (click)="state.addBlock(page.id, item.type)"
                            class="px-2.5 py-1.5 bg-white border border-gray-100 rounded-lg text-xs font-bold text-gray-600 hover:border-theme-primary-200 hover:text-theme-primary-600 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95 flex items-center">
                      <span class="mr-1 text-theme-primary-400">+</span>{{ item.label }}
                    </button>
                  }
                </div>
              </div>
            </div>
          </div>
        }

        <button (click)="state.addPage()"
                class="w-full py-5 sm:py-6 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 hover:text-theme-primary-500 hover:border-theme-primary-200 hover:bg-theme-primary-50/20 transition-all duration-300 font-bold flex flex-col items-center justify-center space-y-2 group">
          <div class="p-2.5 bg-gray-50 rounded-xl group-hover:bg-theme-primary-50 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4"/>
            </svg>
          </div>
          <span class="tracking-tight">Neue Seite hinzufügen</span>
        </button>

        <!-- Cross-field validations (form-level verification) -->
        <div class="mt-5 bg-white rounded-2xl shadow-lg shadow-gray-200/50 p-3 sm:p-4 border border-gray-100">
          <div class="flex items-center justify-between mb-1">
            <h3 class="text-sm font-bold text-gray-900 tracking-tight">Formular-Validierung</h3>
            <button (click)="state.addValidation()"
                    class="text-[10px] font-bold text-theme-primary-600 hover:underline uppercase tracking-widest">
              + Regel
            </button>
          </div>
          <p class="text-xs text-gray-400 font-medium mb-4 leading-relaxed">
            Feldübergreifende Prüfungen. Trifft die Bedingung zu, gilt das Formular als ungültig und die Meldung wird angezeigt.
          </p>

          @if (!def.validations?.length) {
            <p class="text-xs text-gray-300 italic">Keine Validierungsregeln definiert.</p>
          }

          <div class="space-y-5">
            @for (validation of def.validations || []; track validation.id) {
              <div class="p-3 sm:p-4 bg-gray-50/50 rounded-2xl border border-gray-100 space-y-3">
                <div class="flex items-center gap-2">
                  <input [(ngModel)]="validation.message" (ngModelChange)="state.touch()"
                         placeholder="Fehlermeldung"
                         class="flex-grow px-3 py-2 rounded-xl border-gray-100 bg-white text-sm font-medium focus:ring-4 focus:ring-theme-primary-50 transition-all"/>
                  <button (click)="state.removeValidation(validation.id)" aria-label="Regel entfernen"
                          class="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                    </svg>
                  </button>
                </div>
                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Ungültig wenn</p>
                <forms-rule-editor [ruleGroup]="validation.when"></forms-rule-editor>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  `
})
export class BuilderCanvasComponent {
  readonly fieldPalette = FIELD_PALETTE;
  readonly structurePalette = STRUCTURE_PALETTE;

  constructor(public state: BuilderStateService, private dialog: MatDialog) {
  }

  updateTitle(title: string) {
    this.state.setDefinition({...this.state.currentDefinition, title});
  }

  updateDescription(description: string) {
    this.state.setDefinition({...this.state.currentDefinition, description});
  }

  /** How many review types this form requires (shown as a badge on the trigger). */
  requiredReviewCount(def: FormDefinition): number {
    return def.requiredReviews?.length ?? 0;
  }

  /** Open the "Erforderliche Prüfungen" dialog; each toggle applies immediately to the form definition. */
  openReviewsDialog(def: FormDefinition) {
    this.dialog.open(RequiredReviewsPopupComponent, {
      width: '32rem',
      maxWidth: '90vw',
      autoFocus: false,
      panelClass: 'cheqme-dialog',
      data: {
        requiredReviews: def.requiredReviews ?? [],
        onChange: (requiredReviews: ReviewType[]) =>
          this.state.setDefinition({...this.state.currentDefinition, requiredReviews})
      }
    });
  }

  /** The Gültigkeitsdauer shown on the trigger — "unbegrenzt" when the form's answers never expire. */
  validityLabel(def: FormDefinition): string {
    const days = def.validityPeriodDays;
    return days && days > 0 ? `${days} Tage` : 'unbegrenzt';
  }

  /** Open the "Gültigkeitsdauer" dialog; the chosen value applies immediately to the form definition. */
  openValidityDialog(def: FormDefinition) {
    this.dialog.open(ValidityPeriodPopupComponent, {
      width: '32rem',
      maxWidth: '90vw',
      autoFocus: false,
      panelClass: 'cheqme-dialog',
      data: {
        validityPeriodDays: def.validityPeriodDays ?? null,
        onChange: (validityPeriodDays: number | null) =>
          this.state.setDefinition({...this.state.currentDefinition, validityPeriodDays})
      }
    });
  }
}
